import * as React from 'react';
import ky from 'ky';
import { usePostMessageHandler } from './PostMessageProvider';
import { AnalyticsAction, AnalyticsEvents } from 'src/types/Events';

interface IAnalyticsContext {
  // TODO: buffer events before ready? Why wait for readiness?
  ready: boolean;
  track: <T extends AnalyticsAction>(action: T, details: AnalyticsEvents[T]) => void;
}

const AnalyticsContext = React.createContext<IAnalyticsContext>({
  ready: false,
  track: () => {
    throw new Error('AnalyticsProvider misconfigured');
  },
});

export const AnalyticsProvider = ({ children }: { children: React.ReactNode }) => {
  const [analyticsEndpoint, setAnalyticsEndpoint] = React.useState<string | null>(null);
  const [analyticsContext, setAnalyticsContext] = React.useState<Record<string, unknown>>({});
  const [activeProductSku, setActiveProductSku] = React.useState('');
  const buffer = React.useRef<{ action: string, details: object }[]>([]);

  usePostMessageHandler('init', (e) => {
    if ('analytics' in e.detail.config) {
      const { analytics } = e.detail.config;
      setAnalyticsEndpoint(analytics.endpoint);
      setAnalyticsContext(analytics.context);
    }
  });

  usePostMessageHandler('setActiveProduct', (e) => {
    const { activeProduct } = e.detail;
    setActiveProductSku(activeProduct);
  });

  const ready = React.useMemo(() => {
    return !!analyticsEndpoint && !!activeProductSku;
  }, [analyticsEndpoint, activeProductSku]);

  const track = React.useCallback((action: string, details: object) => {
    if (!ready || !analyticsEndpoint) {
      buffer.current.push({ action, details });
      return;
    }
    const body = new URLSearchParams({
      ...analyticsContext,
      ...details,
      action,
      activeProductSku,
      clientDate: (new Date()).getTime().toString(),
    });

    // Remove undefined values
    for (const [key, value] of body.entries()) {
      if (value === undefined || value === 'undefined') {
        body.delete(key);
      }
    }

    // Prefer beacons so that events fire even when page is transitioning
    if ('sendBeacon' in navigator) {
      navigator.sendBeacon(analyticsEndpoint, body);
    } else {
      ky.post(analyticsEndpoint, { body });
    }
  }, [ready, analyticsEndpoint, analyticsContext, activeProductSku]);

  React.useEffect(() => {
    if (!ready) {
      return;
    }
    while (buffer.current.length) {
      const { action, details } = buffer.current.shift()!; // eslint-disable-line
      track(action, details);
    }
  }, [ready]);

  return (
    <AnalyticsContext.Provider value={{ ready, track }}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => React.useContext(AnalyticsContext);
